import { useApolloClient } from '@apollo/client';
import { useRequestTaxRecalculationMutation } from '@cointracker/graphql-types';
import {
  Checkbox,
  Status,
  ToastContext,
  cn,
  useSidebar,
} from '@cointracker/styleguide';
import { Body3, Body4, Button, H5 } from '@cointracker/styleguide/src/LoggedIn';
import { QuestionMark } from '@phosphor-icons/react';
import { useContext, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useNavigate } from 'react-router-dom';
import { useAnalytics } from 'src/app/analytics';
import { URLS } from 'src/common/urls';
import { getCostBasisStatus } from 'src/components/CostBasisProvider/queries/getCostBasisStatus.graphql'; // use src/
import { useTaxReadiness } from '../../hooks/useTaxReadiness';
import { useTaxReadinessStore } from '../utils';
import { GatherCryptoContent } from './GatherCryptoContent';
import { GenerateFormsContent } from './GenerateFormsContent';
import { GetInSyncContent } from './GetInSyncContent';
import { HelpScreen } from './HelpScreen';
import { OptimizeTaxSavingsContent } from './OptimiseTaxSavingsContent';

export const TaxReadinessStatusSheetContent = () => {
  const analytics = useAnalytics();
  const { t } = useTranslation();
  const {
    currentActiveStepNumber,
    completedStepsCount,
    addWalletsStepChecked,
    syncWalletsStepChecked,
    optimizeTaxSavingsStepChecked,
    reviewTaxFormsStepChecked,
    errorsCount,
    transactionCount,
    automaticallySyncedWalletsCount,
    syncIssuesCount,
    taxYear,
  } = useTaxReadiness();
  const { setIsTaxReadinessSheetOpen } = useSidebar();
  const {
    setAddWalletsCheckedByUser,
    setSyncWalletsCheckedByUser,
    setOptimizeTaxSavingsCheckedByUser,
    setReviewTaxFormsCheckedByUser,
  } = useTaxReadinessStore();
  const navigate = useNavigate();
  const [showHelpScreen, setShowHelpScreen] = useState(false);

  const onMarkAsDone = () => {
    analytics.track('Tax Readiness Clicked', {
      action: 'Mark all as done',
      step_1_completed: addWalletsStepChecked,
      step_2_completed: syncWalletsStepChecked,
      step_3_completed: optimizeTaxSavingsStepChecked,
      step_4_completed: reviewTaxFormsStepChecked,
    });
    setAddWalletsCheckedByUser(true);
    setSyncWalletsCheckedByUser(true);
    setOptimizeTaxSavingsCheckedByUser(true);
    setReviewTaxFormsCheckedByUser(true);
    window.location.href = URLS.TAX_PAGE;
  };
  const { showNotification } = useContext(ToastContext);
  const client = useApolloClient();
  const [recalculateTaxes] = useRequestTaxRecalculationMutation({
    onCompleted: (data) => {
      if (data.requestTaxRecalculation?.success) {
        showNotification({
          message: t('navigation.tax_readiness.request_successful'),
          status: Status.Success,
        });
      } else {
        showNotification({
          message: t('navigation.tax_readiness.mutation_error'),
          status: Status.Error,
        });
      }
    },
  });

  const onContinue = () => {
    analytics.track('Tax Readiness Clicked', {
      action: 'Continue',
      step_1_completed: addWalletsStepChecked,
      step_2_completed: syncWalletsStepChecked,
      step_3_completed: optimizeTaxSavingsStepChecked,
      step_4_completed: reviewTaxFormsStepChecked,
    });
    if (completedStepsCount === 4 || currentActiveStepNumber === 4)
      return setIsTaxReadinessSheetOpen(false);

    switch (currentActiveStepNumber) {
      case 1:
        setAddWalletsCheckedByUser(true);
        break;
      case 2:
        setSyncWalletsCheckedByUser(true);
        break;
      case 3:
        setOptimizeTaxSavingsCheckedByUser(true);
        break;
      case 4:
        setReviewTaxFormsCheckedByUser(true);
        break;
    }
  };

  const navigateToPageAndCloseSheet = (page: string) => {
    navigate(page);
    setIsTaxReadinessSheetOpen(false);
  };

  const onCheckLastStep = (checked: boolean) => {
    setReviewTaxFormsCheckedByUser(checked);
    if (checked && currentActiveStepNumber === 4) {
      setIsTaxReadinessSheetOpen(false);
      window.location.href = URLS.TAX_PAGE;
    }
  };

  const onRecalculate = async () => {
    await recalculateTaxes();
    await client.refetchQueries({
      include: [getCostBasisStatus],
    });
  };

  const handleHelpClick = () => {
    setShowHelpScreen(true);
  };

  const trackAndSetIsCompleted = (
    setIsCompleted: (isCompleted: boolean) => void,
    stepName: string,
  ) => {
    return (isCompleted: boolean) => {
      analytics.track('Tax Readiness Clicked', {
        action: `User Checked Step - ${stepName} - ${isCompleted}`,
        step_1_completed: addWalletsStepChecked,
        step_2_completed: syncWalletsStepChecked,
        step_3_completed: optimizeTaxSavingsStepChecked,
        step_4_completed: reviewTaxFormsStepChecked,
      });
      setIsCompleted(isCompleted);
    };
  };

  return (
    <div className="flex flex-col gap-48 p-40">
      {showHelpScreen ? (
        <HelpScreen onBack={() => setShowHelpScreen(false)} />
      ) : (
        <>
          <div className="flex flex-col gap-8">
            <H5>
              {t('navigation.tax_readiness.sheet.title', { value: taxYear })}
            </H5>

            <Body4 className="my-4 text-text-secondary">
              {t('navigation.tax_readiness.sheet.description_1')}
            </Body4>

            <Body4 className="mb-6 mt-2 text-text-secondary">
              {t('navigation.tax_readiness.sheet.disclaimer')}
            </Body4>
          </div>

          <div className="mb-8 flex flex-col gap-12">
            <TaxReadinessStep
              id={1}
              title={t('navigation.tax_readiness.sheet.step_1')}
              isCompleted={addWalletsStepChecked}
              isExpanded={currentActiveStepNumber === 1}
              setIsCompleted={trackAndSetIsCompleted(
                setAddWalletsCheckedByUser,
                'Gather all your crypto',
              )}
            >
              <GatherCryptoContent
                onClickPrimary={() => {
                  analytics.track('Tax Readiness Clicked', {
                    action: 'Review added wallets',
                    step_1_completed: addWalletsStepChecked,
                    step_2_completed: syncWalletsStepChecked,
                    step_3_completed: optimizeTaxSavingsStepChecked,
                    step_4_completed: reviewTaxFormsStepChecked,
                  });
                  navigateToPageAndCloseSheet(URLS.WALLETS_PAGE);
                }}
              />
            </TaxReadinessStep>

            <TaxReadinessStep
              id={2}
              title={t('navigation.tax_readiness.sheet.step_2')}
              isCompleted={syncWalletsStepChecked}
              isExpanded={currentActiveStepNumber === 2}
              setIsCompleted={trackAndSetIsCompleted(
                setSyncWalletsCheckedByUser,
                'Get in sync',
              )}
            >
              <GetInSyncContent
                onClickPrimary={() => {
                  analytics.track('Tax Readiness Clicked', {
                    action: 'Review account health',
                    step_1_completed: addWalletsStepChecked,
                    step_2_completed: syncWalletsStepChecked,
                    step_3_completed: optimizeTaxSavingsStepChecked,
                    step_4_completed: reviewTaxFormsStepChecked,
                  });
                  navigateToPageAndCloseSheet(URLS.RECONCILIATION);
                }}
                automaticallySyncedWalletsCount={
                  automaticallySyncedWalletsCount
                }
                syncIssuesCount={syncIssuesCount}
              />
            </TaxReadinessStep>

            <TaxReadinessStep
              id={3}
              title={t('navigation.tax_readiness.sheet.step_3')}
              isCompleted={optimizeTaxSavingsStepChecked}
              isExpanded={currentActiveStepNumber === 3}
              setIsCompleted={trackAndSetIsCompleted(
                setOptimizeTaxSavingsCheckedByUser,
                'Optimize tax savings',
              )}
            >
              <OptimizeTaxSavingsContent
                onClickPrimary={() => {
                  analytics.track('Tax Readiness Clicked', {
                    action: 'Review transactions',
                    step_1_completed: addWalletsStepChecked,
                    step_2_completed: syncWalletsStepChecked,
                    step_3_completed: optimizeTaxSavingsStepChecked,
                    step_4_completed: reviewTaxFormsStepChecked,
                  });
                  window.location.href =
                    URLS.TRANSACTIONS_PAGE_NEEDS_REVIEW +
                    `+from%3A${taxYear}-01-01+to%3A${taxYear}-12-31`;
                }}
                onClickSecondary={onRecalculate}
                transactionCount={transactionCount}
                errorsCount={errorsCount}
              />
            </TaxReadinessStep>

            <TaxReadinessStep
              id={4}
              title={t('navigation.tax_readiness.sheet.step_4')}
              isCompleted={reviewTaxFormsStepChecked}
              isExpanded={currentActiveStepNumber === 4}
              setIsCompleted={trackAndSetIsCompleted(
                onCheckLastStep,
                'Generate forms',
              )}
            >
              <GenerateFormsContent
                onClickPrimary={() => {
                  analytics.track('Tax Readiness Clicked', {
                    action: 'Generate forms',
                    step_1_completed: addWalletsStepChecked,
                    step_2_completed: syncWalletsStepChecked,
                    step_3_completed: optimizeTaxSavingsStepChecked,
                    step_4_completed: reviewTaxFormsStepChecked,
                  });
                  setReviewTaxFormsCheckedByUser(true);
                  window.location.href = URLS.TAX_PAGE;
                }}
              />
            </TaxReadinessStep>
          </div>

          <div className="mt-auto flex items-center justify-between gap-[12px] pb-16 pt-8">
            <button
              className="text-sm flex cursor-pointer items-center border-none bg-transparent text-text-link"
              onClick={handleHelpClick}
            >
              {t('button.get_help')}{' '}
              <QuestionMark size={18} className="mb-4 ml-4" weight="bold" />
            </button>
            <div className="flex gap-12">
              {completedStepsCount < 4 && (
                <Button size="small" onClick={onMarkAsDone}>
                  {t('button.mark_all_as_done')}
                </Button>
              )}
              <Button size="small" variant="line" onClick={onContinue}>
                {completedStepsCount < 4
                  ? t('button.continue')
                  : t('button.done')}
              </Button>
            </div>
          </div>
        </>
      )}
    </div>
  );
};

interface StepProps {
  id: number;
  title: string;
  isCompleted?: boolean;
  isExpanded?: boolean;
  children: React.ReactNode;
  setIsCompleted: (isCompleted: boolean) => void;
}

const TaxReadinessStep = ({
  id,
  title,
  isCompleted = false,
  isExpanded = false,
  children,
  setIsCompleted,
}: StepProps) => {
  return (
    <div className="rounded-[14px] border border-line-card p-16">
      <div className="flex items-center justify-between">
        <Body3>
          {id}. {title}
        </Body3>
        <Checkbox
          checked={isCompleted}
          onClick={() => setIsCompleted(!isCompleted)}
        />
      </div>
      <div
        className={cn(
          'overflow-hidden',
          isExpanded
            ? 'mt-16 max-h-[1000px] opacity-100 transition-all duration-300 ease-in'
            : 'mt-0 max-h-0 opacity-0 transition-all duration-200 ease-out',
        )}
      >
        {children}
      </div>
    </div>
  );
};
